import TypesenseInstantSearchAdapter from 'typesense-instantsearch-adapter';
import {
  DEFAULT_SEARCH_ITEMS_PER_PAGE,
  HOME_SEARCH_INDEX_ID,
  MAIN_SEARCH_INDEX_ID,
} from '../constants';
import {
  getTypesenseServerConfig,
  MAIN_SEARCH_ADDITIONAL_PARAMETERS,
  NUMERIC_ONLY_MAIN_SEARCH_PARAMETERS,
  TYPESENSE_COLLECTION,
} from '../typesense-search-config';
import { wrapSearchClientWithNumericRouting } from '../search-client-numeric-routing';

const server = getTypesenseServerConfig();

/**
 * `enable_analytics: false` — default for all non-header searches.
 * TypesenseInstantSearchAdapter forwards `additionalSearchParameters` directly
 * to Typesense; injecting the flag here (adapter level) is the only reliable
 * way to control it. Setting it in InstantSearch `params` at wrapper level
 * has no effect because the adapter does not forward unknown IS params.
 */
const makeMainAdapter = (enableAnalytics: boolean) =>
  new TypesenseInstantSearchAdapter({
    server,
    additionalSearchParameters: {
      ...MAIN_SEARCH_ADDITIONAL_PARAMETERS,
      per_page: DEFAULT_SEARCH_ITEMS_PER_PAGE,
      enable_analytics: enableAnalytics,
    } as Record<string, unknown>,
  });

const makeNumericAdapter = (enableAnalytics: boolean) =>
  new TypesenseInstantSearchAdapter({
    server,
    additionalSearchParameters: {
      ...NUMERIC_ONLY_MAIN_SEARCH_PARAMETERS,
      per_page: DEFAULT_SEARCH_ITEMS_PER_PAGE,
      enable_analytics: enableAnalytics,
    } as Record<string, unknown>,
  });

const mainAdapterNoAnalytics = makeMainAdapter(false);
const numericAdapterNoAnalytics = makeNumericAdapter(false);

const mainAdapterAnalytics = makeMainAdapter(true);
const numericAdapterAnalytics = makeNumericAdapter(true);

/**
 * Migrates stale sort values stored in old browser URLs to the current
 * canonical form. Only explicit known old values are rewritten; everything
 * else passes through untouched.
 */
const SORT_MIGRATIONS: Record<string, string> = {
  // Old price sorts had _text_match before sale_price.
  'in_stock:desc,_text_match:desc,sale_price:asc':
    'in_stock:desc,sale_price:asc,_text_match:desc',
  'in_stock:desc,_text_match:desc,sale_price:desc':
    'in_stock:desc,sale_price:desc,_text_match:desc',
  // Old relevance sorts (various intermediate forms).
  'popularity_score:desc':
    'in_stock:desc,_text_match:desc,popularity_score:desc',
  'in_stock:desc,popularity_score:desc':
    'in_stock:desc,_text_match:desc,popularity_score:desc',
};

function normalizeSort(sortBy: string): string {
  if (!sortBy) return sortBy;
  return SORT_MIGRATIONS[sortBy] ?? sortBy;
}

/**
 * Translates frontend index IDs (e.g. "products", "products_home") to the
 * actual Typesense collection name before requests reach the adapter.
 * This lets InstantSearch use stable, routing-friendly names while Typesense
 * always queries the correct collection.
 * Sort normalization is applied here so stale URL values are also corrected.
 */
function remapIndexName(indexName: string): string {
  const frontendIds = [MAIN_SEARCH_INDEX_ID, HOME_SEARCH_INDEX_ID];
  for (const id of frontendIds) {
    if (indexName === id) return TYPESENSE_COLLECTION;
    const sortPrefix = `${id}/sort/`;
    if (indexName.startsWith(sortPrefix)) {
      const sortBy = indexName.slice(sortPrefix.length);
      return `${TYPESENSE_COLLECTION}/sort/${normalizeSort(sortBy)}`;
    }
    if (indexName.startsWith(id + '/'))
      return TYPESENSE_COLLECTION + indexName.slice(id.length);
  }
  return indexName;
}

function withCollectionRemap(baseClient: any): any {
  return {
    ...baseClient,
    search: (requests: any[]) => {
      const remapped = requests.map((req) => ({
        ...req,
        indexName: remapIndexName(req.indexName),
      }));
      return baseClient.search(remapped);
    },
  };
}

/** Analytics disabled — used for all non-header and wildcard queries. */
const noAnalyticsClient = withCollectionRemap(
  wrapSearchClientWithNumericRouting(
    mainAdapterNoAnalytics.searchClient,
    numericAdapterNoAnalytics.searchClient,
  ),
);

/** Analytics enabled — used only for real (non-wildcard) header searches. */
const analyticsClient = withCollectionRemap(
  wrapSearchClientWithNumericRouting(
    mainAdapterAnalytics.searchClient,
    numericAdapterAnalytics.searchClient,
  ),
);

/**
 * Returns true when the current URL indicates the search was initiated from
 * the main header search box (`tsa=header` query param is present).
 */
function isHeaderSearchContext(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('tsa') === 'header';
}

/**
 * Returns true when at least one request in the batch carries a real, typed
 * query (non-empty and not the wildcard `*`). Wildcard queries happen on page
 * load / filter-only views and must never count as analytics events.
 */
function hasRealSearchQuery(requests: any[]): boolean {
  return requests.some((req) => {
    const q = String(req?.params?.query ?? '').trim();
    return q.length > 0 && q !== '*';
  });
}

/**
 * Main search client used by all InstantSearch providers.
 * Routes to the analytics-enabled adapter only when:
 *   1. The URL contains `tsa=header` (search came from the header search box), AND
 *   2. The query is a real typed term (not empty or wildcard).
 * Every other call — sidebar searches, filter-only views, home-page carousels,
 * wildcard `*` loads — goes through the analytics-disabled adapter.
 */
export const productSearchClient = {
  ...noAnalyticsClient,
  search: (requests: any[]) => {
    if (isHeaderSearchContext() && hasRealSearchQuery(requests)) {
      return analyticsClient.search(requests);
    }
    return noAnalyticsClient.search(requests);
  },
};

export const productSearchClientNoEmptySearch = {
  ...productSearchClient,
  search: (requests: any) => {
    if (requests.every(({ params }: any) => !params.query)) {
      return Promise.resolve({
        results: requests.map(() => ({
          hits: [],
          nbHits: 0,
          nbPages: 0,
          page: 0,
          processingTimeMS: 0,
          hitsPerPage: 0,
          exhaustiveNbHits: false,
          query: '',
          params: '',
        })),
      });
    }

    return productSearchClient.search(requests);
  },
};

export const getProducts = (query: string) =>
  productSearchClient.search([
    {
      indexName: MAIN_SEARCH_INDEX_ID,
      params: { query },
    },
  ]);
